import React, { useEffect, useState } from 'react';
import { motion } from 'framer-motion';
import { SlideStage } from '../../components/deck/SlideStage';
import { cashFlowMilestone, executionPath, opportunityShare, revenueYears, yearThreeScale } from '../../data/deckSlide12';
import { readDeckAudience } from '../../data/investorAccess';
const VEXU_LOGO = 'https://cdn.magicpatterns.com/uploads/3A8d459aXLumR8KKFCQtrD/vexu_logo.svg';
const ease = [0.22, 0.61, 0.36, 1] as const;
type Stage = 0 | 1 | 2 | 3 | 4 | 5 | 6;

/** Chart geometry on the fixed 1600×900 canvas. */
const BASELINE = 656;
const MAX_BAR = 300;
const BAR_W = 108;
const BAR_X = [772, 1004, 1236];
export function FinancialOutlookSlide() {
  const [stage, setStage] = useState<Stage>(0);
  // Marketing view omits the funding step; the path starts at launch.
  const path = readDeckAudience() === 'marketing' ? executionPath.filter(step => !step.includes('SAFE')) : executionPath;
  const fundingLed = path[0]?.includes('SAFE');
  useEffect(() => {
    const timers: number[] = [];
    const at = (ms: number, next: Stage) => timers.push(window.setTimeout(() => setStage(next), ms));
    at(600, 1); // Year 1
    at(1600, 2); // Year 2
    at(2600, 3); // cash-flow milestone
    at(3600, 4); // Year 3
    at(4900, 5); // Year-3 operating scale + share
    at(6600, 6); // execution path + takeaway
    return () => timers.forEach(id => window.clearTimeout(id));
  }, []);
  const shown = (s: Stage) => stage >= s;
  const barShown = (i: number) => shown((i === 2 ? 4 : i + 1) as Stage);
  const max = revenueYears[revenueYears.length - 1].value;
  return <SlideStage data-id="element-2266" data-mp="1zkgo2y2dxub">
      <div className="deck-type absolute inset-0 overflow-hidden bg-[#FAFCF9]" data-id="element-2267" data-mp="202olwu3a0i2">
        <div className="absolute inset-0" style={{
        background: 'radial-gradient(ellipse 80% 65% at 6% -18%, rgba(0,200,83,0.06) 0%, rgba(250,252,249,0) 60%), linear-gradient(180deg, #FBFDFA 0%, #F5F8F3 58%, #EFF3ED 100%)'
      }} data-id="element-2268" data-mp="3t4aw4yyqmpzx" />

        {/* Brand header */}
        <motion.div className="absolute flex items-center gap-3" style={{
        left: 112,
        top: 56
      }} initial={{
        opacity: 0
      }} animate={{
        opacity: 1
      }} transition={{
        duration: 0.8,
        ease
      }} data-id="element-2269" data-mp="615m3nie73sw">
          <img src={VEXU_LOGO} alt="" className="w-7 h-7 object-contain" data-id="element-2270" data-mp="k1q7mrfozdq3" />
          <span className="text-[16px] font-bold tracking-tight text-[#0B1712]" data-id="element-2271" data-mp="3jgbs6uuzc0g8">
            Vexu.ai
          </span>
        </motion.div>
        <motion.span className="absolute text-[11px] font-semibold uppercase tracking-[0.28em] text-[#8A998F]" style={{
        right: 112,
        top: 62
      }} initial={{
        opacity: 0
      }} animate={{
        opacity: 1
      }} transition={{
        duration: 0.8,
        ease
      }} data-id="element-2272" data-mp="3nh5ddgh1f15f">
          13 · Financial Outlook
        </motion.span>

        {/* Headline */}
        <h1 className="absolute text-[40px] font-semibold leading-[1.16] tracking-[-0.02em] text-[#0B1712]" style={{
        left: 112,
        top: 126,
        width: 540
      }} data-id="element-2273" data-mp="34r5vpenj0oc1">
          {['A focused launch.', 'A scalable revenue path.'].map((line, i) => <motion.span key={line} className="block" initial={{
          opacity: 0,
          y: 14
        }} animate={{
          opacity: 1,
          y: 0
        }} transition={{
          duration: 0.9,
          delay: 0.15 + i * 0.15,
          ease
        }} data-id="element-2274" data-mp="2s9mmuym9j2vk" data-mpi={i}>
              {line}
            </motion.span>)}
        </h1>
        <motion.p className="absolute text-[16px] leading-relaxed text-[#5A6B60]" style={{
        left: 112,
        top: 236,
        width: 500
      }} initial={{
        opacity: 0
      }} animate={{
        opacity: 1
      }} transition={{
        duration: 0.9,
        delay: 0.7,
        ease
      }} data-id="element-2275" data-mp="29ilsknw37vic">
          Growth is driven by paid VEXU App subscribers, business adoption and
          expanding usage across both products.
        </motion.p>

        {/* LEFT — Year-3 operating scale */}
        <motion.div className="absolute" style={{
        left: 112,
        top: 356,
        width: 520
      }} initial={{
        opacity: 0,
        y: 10
      }} animate={{
        opacity: shown(5) ? 1 : 0,
        y: shown(5) ? 0 : 10
      }} transition={{
        duration: 0.9,
        ease
      }} data-id="element-2276" data-mp="1qlu13r6e6a37">
          <p className="text-[11px] font-semibold uppercase tracking-[0.22em] text-[#8A998F]" data-id="element-2277" data-mp="3gvebpib1wp76">
            Year 3 · Base case
          </p>
          <div className="mt-7 flex gap-16" data-id="element-2278" data-mp="36xqac9ixwjg1">
            {yearThreeScale.map(item => <div key={item.label} data-id="element-2279" data-mp="3bwtcnm7jcbkb">
                <p className="text-[40px] font-semibold leading-none tracking-[-0.03em] text-[#0B1712] tabular-nums" data-id="element-2280" data-mp="3bxw2rrx1a9z6">
                  {item.value}
                </p>
                <p className="mt-2.5 text-[13.5px] text-[#7C8B82]" data-id="element-2281" data-mp="32vv7h0ld1y05">
                  {item.label}
                </p>
              </div>)}
          </div>
        </motion.div>

        {/* LEFT — share of the illustrative opportunity */}
        <motion.div className="absolute border-l pl-6" style={{
        left: 112,
        top: 522,
        width: 470,
        borderColor: 'rgba(0,153,64,0.4)'
      }} initial={{
        opacity: 0,
        x: -6
      }} animate={{
        opacity: shown(5) ? 1 : 0,
        x: shown(5) ? 0 : -6
      }} transition={{
        duration: 0.9,
        delay: 0.35,
        ease
      }} data-id="element-2282" data-mp="1149pojn9sksw">
          <p className="text-[14.5px] text-[#5A6B60]" data-id="element-2283" data-mp="1f8quhrlh5vi0">
            {opportunityShare.lead}
          </p>
          <p className="mt-1 text-[46px] font-semibold leading-none tracking-[-0.03em] text-vexu-700 tabular-nums" data-id="element-2284" data-mp="1txn0lj3z0yfz">
            {opportunityShare.value}
          </p>
          <p className="mt-3 text-[13.5px] leading-snug text-[#7C8B82]" data-id="element-2285" data-mp="23062o0zxn716">
            {opportunityShare.label}
          </p>
        </motion.div>

        {/* RIGHT — the three-year revenue trajectory */}
        <div className="absolute" style={{
        left: 700,
        top: 0
      }} data-id="element-2286" data-mp="8l3i8hpqqf54" />

        {/* baseline */}
        <motion.div className="absolute h-px bg-[#0B1712]/[0.12]" style={{
        left: 720,
        top: BASELINE,
        width: 700
      }} initial={{
        opacity: 0
      }} animate={{
        opacity: shown(1) ? 1 : 0
      }} transition={{
        duration: 0.8,
        ease
      }} data-id="element-2287" data-mp="4l1nksifalzc" />

        {revenueYears.map((item, i) => {
        const height = Math.round(item.value / max * MAX_BAR);
        const isFinal = i === revenueYears.length - 1;
        return <div key={item.year} data-id="element-2288" data-mp="2ijxy0swirwsy" data-mpi={i}>
              {/* value */}
              <motion.p className={`absolute text-center tabular-nums ${isFinal ? 'text-[40px] font-semibold text-[#0B1712]' : 'text-[26px] font-semibold text-[#33463B]'}`} style={{
            left: BAR_X[i] - 40,
            top: BASELINE - height - (isFinal ? 62 : 46),
            width: BAR_W + 80,
            letterSpacing: '-0.02em'
          }} initial={{
            opacity: 0,
            y: 8
          }} animate={{
            opacity: barShown(i) ? 1 : 0,
            y: barShown(i) ? 0 : 8
          }} transition={{
            duration: 0.7,
            delay: 0.35,
            ease
          }} data-id="element-2289" data-mp="2mncoyelzxxl6">
                {item.display}
              </motion.p>

              {/* bar */}
              <motion.div className="absolute rounded-t-[6px]" style={{
            left: BAR_X[i],
            width: BAR_W,
            bottom: 900 - BASELINE,
            backgroundColor: isFinal ? '#009940' : 'rgba(0,153,64,0.22)',
            boxShadow: isFinal ? '0 18px 40px -22px rgba(0,153,64,0.75)' : undefined
          }} initial={{
            height: 0
          }} animate={{
            height: barShown(i) ? height : 0
          }} transition={{
            duration: 0.9,
            ease
          }} data-id="element-2290" data-mp="powyr0832wf3" />

              {/* year */}
              <motion.div className="absolute text-center" style={{
            left: BAR_X[i],
            top: BASELINE + 16,
            width: BAR_W
          }} initial={{
            opacity: 0
          }} animate={{
            opacity: barShown(i) ? 1 : 0
          }} transition={{
            duration: 0.6,
            delay: 0.3,
            ease
          }} data-id="element-2291" data-mp="215wopp6ykz5y">
                <p className={`text-[13px] ${isFinal ? 'font-semibold text-[#0B1712]' : 'text-[#7C8B82]'}`} data-id="element-2292" data-mp="vpm7md3rr5jb">
                  {item.year}
                </p>
                {isFinal && <p className="mt-1 text-[10.5px] font-semibold uppercase tracking-[0.18em] text-vexu-700" data-id="element-2293" data-mp="1ngjw14z3s3yz">
                    Base case
                  </p>}
              </motion.div>
            </div>;
      })}

        {/* cash-flow milestone, between Year 1 and Year 2 */}
        <motion.div className="absolute" style={{
        left: 886,
        top: 566,
        width: 118
      }} initial={{
        opacity: 0
      }} animate={{
        opacity: shown(3) ? 1 : 0
      }} transition={{
        duration: 0.8,
        ease
      }} data-id="element-2294" data-mp="1jef2e68nn7cc">
          <p className="text-[13px] font-semibold text-[#0B1712]" data-id="element-2295" data-mp="7xyls9ld6knw">
            {cashFlowMilestone.when}
          </p>
          <p className="mt-1 text-[11px] leading-snug text-[#7C8B82]" data-id="element-2296" data-mp="3rmtqc9j2r36m">
            {cashFlowMilestone.label}
          </p>
          {/* tick down to the timeline */}
          <span className="absolute w-px" style={{
          left: 54,
          top: 52,
          height: BASELINE - 566 - 56,
          backgroundColor: 'rgba(0,153,64,0.3)'
        }} data-id="element-2297" data-mp="9gfd6csmqx9a" />
          <span className="absolute rounded-full" style={{
          left: 51,
          top: BASELINE - 566 - 3,
          width: 7,
          height: 7,
          backgroundColor: '#009940'
        }} data-id="element-2298" data-mp="37zckejp8nikq" />
        </motion.div>

        {/* Execution path */}
        <motion.div className="absolute flex items-center gap-4 border-t border-[#0B1712]/[0.09] pt-6" style={{
        left: 112,
        right: 112,
        bottom: 108
      }} initial={{
        opacity: 0
      }} animate={{
        opacity: shown(6) ? 1 : 0
      }} transition={{
        duration: 0.9,
        delay: 0.2,
        ease
      }} data-id="element-2299" data-mp="2u6nynadj9l0p">
          {path.map((step, i) => <React.Fragment key={step} data-id="element-2300" data-mp="38sw6qj8vnavz" data-mpi={i}>
              {i > 0 && <span className="text-[12px] text-[#C3CCC5]" data-id="element-2301" data-mp="38rl2kibis14c">→</span>}
              <span className={`text-[15px] ${i === 0 && fundingLed ? 'font-semibold text-vexu-700' : i === path.length - 1 ? 'font-semibold text-[#0B1712]' : 'text-[#33463B]'}`} data-id="element-2302" data-mp="33ub5qhlswn6h">
                {step}
              </span>
            </React.Fragment>)}
        </motion.div>

        {/* Takeaway */}
        <motion.p className="absolute text-[15px] font-medium text-[#33463B]" style={{
        left: 112,
        bottom: 48
      }} initial={{
        opacity: 0
      }} animate={{
        opacity: shown(6) ? 1 : 0
      }} transition={{
        duration: 0.9,
        delay: 0.5,
        ease
      }} data-id="element-2303" data-mp="2q2wks58c2bws">
          Meaningful scale without requiring mass-market penetration.
        </motion.p>
      </div>
    </SlideStage>;
}